import { useIntl } from 'react-intl';
import {
  ListingProduct,
  DeliveryConditionsData,
  DeliveryProductReference,
} from 'types/ListingData';
import { Button } from 'ui/Button';
import dayjs from 'dayjs';
import { Label } from 'ui/Label';

interface DeliveryConditionsSummaryProps {
  deliveryConditions: DeliveryConditionsData[];
  products: ListingProduct[];
  onEditDelivery: (deliveryId: number) => void;
  onDeleteDelivery: (deliveryId: number) => void;
}

export const DeliveryConditionsSummary = ({
  deliveryConditions,
  products,
  onEditDelivery,
  onDeleteDelivery,
}: DeliveryConditionsSummaryProps) => {
  const intl = useIntl();
  const deliveryType = deliveryConditions[0]?.delivery_type;

  const translateUnit = (unit: string | undefined) => {
    if (!unit) return '';
    return intl.formatMessage({ id: `enum.${unit.toUpperCase()}` });
  };

  if (deliveryType === 'detailed_delivery') {
    return (
      <>
        {deliveryConditions.map((delivery, index) => (
          <div key={delivery.id || index} className="summary-delivery-item">
            <div className="delivery-tag">{index + 1}</div>
            <div className="delivery-info">
              <div className="delivery-address">
                {delivery.delivery_address ||
                  intl.formatMessage({ id: 'listings.form.delivery_type' })}
              </div>
              {delivery.delivery_start_date && (
                <div className="delivery-details">
                  <span>
                    {dayjs(delivery.delivery_start_date).format('DD.MM.YYYY')} -{' '}
                    {dayjs(delivery.delivery_end_date).format('DD.MM.YYYY')}
                  </span>
                </div>
              )}
              {delivery.products && delivery.products.length > 0 && (
                <div className="delivery-products">
                  <Label
                    className="delivery-products-title"
                    label={intl.formatMessage({
                      id: 'listings.selected_products',
                    })}
                    title={true}
                  />
                  {delivery.products.map((product, pIndex) => {
                    const ref = product as DeliveryProductReference;
                    const productId = ref.listing_product_id ?? ref.id;
                    const fullProduct: ListingProduct | undefined =
                      products.find(
                        (p) =>
                          String(p.id) === String(productId) ||
                          (ref.category_id && p.category_id === ref.category_id)
                      ) ?? ref.listing_product;
                    const quantity =
                      ref.delivery_quantity ?? ref.product_quantity ?? 0;
                    const unit =
                      fullProduct?.storage_unit?.short ??
                      fullProduct?.storage_unit?.name ??
                      '';
                    return (
                      <div key={pIndex} className="delivery-product-row">
                        {`${fullProduct?.name || fullProduct?.category?.name || intl.formatMessage({ id: 'general.product' })} - (${quantity} ${unit}.)`}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="delivery-actions">
              <Button
                label={intl.formatMessage({ id: 'general.edit' })}
                onClick={() => onEditDelivery(delivery.id || index)}
                className="action-button"
              />
              <Button
                label={intl.formatMessage({ id: 'general.delete' })}
                onClick={() => onDeleteDelivery(delivery.id || index)}
                className="action-button"
              />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (deliveryType === 'delivery_conditions') {
    const condition = deliveryConditions[0];
    const isFromContractSigning =
      condition.contract_period_type === 'from_contract_signing';

    return (
      <div className="summary-delivery-card">
        <div className="delivery-card-title">
          {isFromContractSigning
            ? intl.formatMessage({
                id: 'listings.contract_period_type.from_contract_signing',
              })
            : condition.contract_period_type
              ? intl.formatMessage({
                  id: `listings.contract_period_type.${condition.contract_period_type}`,
                })
              : '-'}
        </div>
        {condition.contract_start_date && (
          <div className="summary-field">
            <span className="summary-field-label">
              {intl.formatMessage({ id: 'listings.contract_period' })}:
            </span>
            <span className="summary-field-value">
              {dayjs(condition.contract_start_date).format('DD.MM.YYYY')} -{' '}
              {dayjs(condition.contract_end_date).format('DD.MM.YYYY')}
            </span>
          </div>
        )}
        {condition.delivery_period_value && (
          <div className="summary-field">
            <span className="summary-field-label">
              {intl.formatMessage({ id: 'listings.delivery_period' })}:
            </span>
            <span className="summary-field-value">
              {condition.delivery_period_value}{' '}
              {translateUnit(condition.delivery_period_unit)}
            </span>
          </div>
        )}
        {condition.delivery_frequency_value && (
          <div className="summary-field">
            <span className="summary-field-label">
              {intl.formatMessage({ id: 'listings.delivery_frequency' })}:
            </span>
            <span className="summary-field-value">
              {condition.delivery_frequency_value}{' '}
              {translateUnit(condition.delivery_frequency_unit)}
            </span>
          </div>
        )}
        <div className="delivery-actions">
          <Button
            label={intl.formatMessage({ id: 'general.edit' })}
            onClick={() => onEditDelivery(condition.id || 0)}
            className="action-button"
          />
          <Button
            label={intl.formatMessage({ id: 'general.delete' })}
            onClick={() => onDeleteDelivery(condition.id || 0)}
            className="action-button"
          />
        </div>
      </div>
    );
  }

  if (deliveryType === 'free_form') {
    const condition = deliveryConditions[0];

    return (
      <div className="summary-delivery-card">
        <div className="summary-field">
          <span className="summary-field-label">
            {intl.formatMessage({ id: 'listings.free_form_description' })}:
          </span>
          <span className="summary-field-value">
            {condition.free_form_description || '-'}
          </span>
        </div>
        {condition.contract_start_date && (
          <div className="summary-field">
            <span className="summary-field-label">
              {intl.formatMessage({ id: 'listings.contract_period' })}:
            </span>
            <span className="summary-field-value">
              {dayjs(condition.contract_start_date).format('DD.MM.YYYY')} -{' '}
              {dayjs(condition.contract_end_date).format('DD.MM.YYYY')}
            </span>
          </div>
        )}
        <div className="delivery-actions">
          <Button
            label={intl.formatMessage({ id: 'general.edit' })}
            onClick={() => onEditDelivery(condition.id || 0)}
            className="action-button"
          />
          <Button
            label={intl.formatMessage({ id: 'general.delete' })}
            onClick={() => onDeleteDelivery(condition.id || 0)}
            className="action-button"
          />
        </div>
      </div>
    );
  }

  return null;
};
